import { HttpClient, HttpParams } from '@angular/common/http';
import { formatDate } from '@angular/common';
import { Injectable, inject, signal } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { ISvcChipOption } from '@solvace/component/svc-controls';
import { AppEnvironmentConfig } from 'projects/config/model/environment.config.model';
import { UserService } from 'projects/lib-shared-core/src/public-api';
import { catchError, forkJoin, map, Observable, of, shareReplay } from 'rxjs';

import {
  SvcModalActionPlanByIdResponse,
  SvcModalActionPlanCombos,
  SvcModalActionPlanCustomFieldTemplate,
  SvcModalActionPlanCreateApiRequest,
  SvcModalActionPlanCreateRequest,
  SvcModalActionPlanCreateResponse,
  SvcModalActionPlanSystemReference,
  SvcModalActionPlanEscalateRequest,
  SvcModalActionPlanRescheduleRequest,
  SvcModalActionPlanUpdateApiRequest,
  SvcModalActionPlanSystem,
  SvcModalActionPlanSystemsResponse,
  SvcModalActionPlanSystemData,
  SvcModalActionPlanTypeOption,
  SvcModalActionPlanPrioritiesResponse,
} from '../interfaces/svc-modal-action-plan.interface';
import { SvcModalActionPlanStatusId } from '../enums/svc-modal-action-plan-status.enum';
import { SvcModalActionPlanTab } from '../enums/svc-modal-action-plan-tab.enum';
import { OBEYA_APPLICATION_IDS } from '../utils/actionplan-obeya-reference.utils';
import { buildOwnerOptions } from '../utils/actionplan-owner-selection.utils';

@Injectable({
  providedIn: 'root',
})
export class SvcModalActionPlanService {
  #http = inject(HttpClient);
  #appConfig = inject(AppEnvironmentConfig);
  #translocoService = inject(TranslocoService);
  #userService = inject(UserService);

  #apiActionPlan = this.#appConfig.APIs.apiUrlActionPlan;
  #apiCustomField = this.#appConfig.APIs.apiUrlCustomField;
  #systemsCache$: Observable<SvcModalActionPlanSystem[]> | null = null;
  #obeyaSystemUniqueIdsCache$: Observable<number[]> | null = null;

  public readonly modalTabIndex = signal<SvcModalActionPlanTab>(SvcModalActionPlanTab.Checklist);
  public readonly modalTotalComments = signal<number>(0);
  public readonly modalHistoryRefreshTick = signal<number>(0);
  public readonly modalLoadedActionPlan = signal<SvcModalActionPlanByIdResponse | null>(null);
  public readonly modalIsLoading = signal<boolean>(false);
  public readonly modalHasChanges = signal<boolean>(false);

  public resetModalState(): void {
    this.modalTabIndex.set(SvcModalActionPlanTab.Checklist);
    this.modalTotalComments.set(0);
    this.modalHistoryRefreshTick.set(0);
    this.modalLoadedActionPlan.set(null);
    this.modalIsLoading.set(false);
    this.modalHasChanges.set(false);
  }

  public selectModalTab(tab: SvcModalActionPlanTab): void {
    this.modalTabIndex.set(tab);
  }

  public setModalCommentsTotal(total: number): void {
    this.modalTotalComments.set(Number(total ?? 0) || 0);
  }

  public refreshModalHistory(): void {
    this.modalHistoryRefreshTick.update((value) => value + 1);
  }

  public setModalLoadedActionPlan(actionPlan: SvcModalActionPlanByIdResponse | null): void {
    this.modalLoadedActionPlan.set(actionPlan);
  }

  public setModalLoading(isLoading: boolean): void {
    this.modalIsLoading.set(!!isLoading);
  }

  public setModalHasChanges(hasChanges: boolean): void {
    this.modalHasChanges.set(!!hasChanges);
  }

  public getCurrentSiteId(): number | undefined {
    return this.#userService.user?.lastSiteId ?? this.#userService.user?.originSiteId;
  }

  public invalidateSystemsListCache(): void {
    this.#systemsCache$ = null;
    this.#obeyaSystemUniqueIdsCache$ = null;
  }

  public getObeyaSystemUniqueIds(): Observable<number[]> {
    if (this.#obeyaSystemUniqueIdsCache$) return this.#obeyaSystemUniqueIdsCache$;

    const siteId = Number(this.getCurrentSiteId() ?? 0);
    if (!Number.isFinite(siteId) || siteId <= 0) return of([]);

    const requests = OBEYA_APPLICATION_IDS.map((applicationId) =>
      this.#http
        .get<Array<{ systemUniqueId?: number; isActive?: boolean }>>(`${this.#apiActionPlan}/system/by-application`, {
          params: new HttpParams().set('siteId', siteId).set('applicationId', applicationId),
          headers: this.#getEnvironmentHeaders(),
        })
        .pipe(catchError(() => of([])))
    );

    this.#obeyaSystemUniqueIdsCache$ = forkJoin(requests).pipe(
      map((responses) => {
        const ids = responses
          .flat()
          .filter((system) => system?.isActive !== false && system?.systemUniqueId != null)
          .map((system) => Number(system.systemUniqueId));
        return Array.from(new Set(ids));
      }),
      catchError(() => of<number[]>([])),
      shareReplay(1)
    );

    return this.#obeyaSystemUniqueIdsCache$;
  }

  public getCombos(): Observable<SvcModalActionPlanCombos> {
    return forkJoin({
      types: this.#getTypeOptions(),
      priorities: this.#getPriorityOptions(),
      systems: this.#getSystems(),
      users: this.#getUsersOptions(),
      teams: this.#getTeamsOptions(),
    }).pipe(
      map(({ types, priorities, systems, users, teams }) => {
        const usersOptions = users ?? [];
        const teamsOptions = teams ?? [];
        return {
          types: types ?? [],
          priorities: priorities ?? [],
          systems: (systems ?? []).map((system) => ({
            label: system?.systemName,
            value: system?.systemUniqueId,
            data: {
              templateId: system?.templateId,
              systemName: system?.systemName,
              systemId: system?.systemId,
            },
          })),
          users: usersOptions,
          teams: teamsOptions,
          owners: buildOwnerOptions(
            usersOptions.map((option) => ({ value: String(option.value ?? ''), label: String(option.label ?? '') })),
            teamsOptions.map((option) => ({ value: Number(option.value), label: String(option.label ?? '') })),
            {
              users: this.#translocoService.translate('Usuários'),
              teams: this.#translocoService.translate('Times'),
            },
          ),
        };
      })
    );
  }

  public createActionPlan(payload: SvcModalActionPlanCreateRequest): Observable<SvcModalActionPlanCreateResponse | number> {
    return this.#http.post<SvcModalActionPlanCreateResponse | number>(
      `${this.#apiActionPlan}/ActionPlan`,
      this.#mapCreateRequestPayload(payload),
      {
        headers: this.#getEnvironmentHeaders(),
      }
    );
  }

  public findActionPlanUniqueIdByActionPlanId(actionPlanId: number): Observable<number | null> {
    const normalizedActionPlanId = Number(actionPlanId ?? 0);
    if (!Number.isFinite(normalizedActionPlanId) || normalizedActionPlanId <= 0) {
      return of(null);
    }

    // `filter` is the free-text search param `/ActionPlan/search/list` actually
    // supports (same contract as the action-plan search screen — see
    // `appendBaseParams`). `generalSearch` was silently ignored by the backend,
    // returning the unfiltered list and making the id match below always fail.
    let params = new HttpParams()
      .set('pageIndex', 1)
      .set('pageSize', 25)
      .set('filter', `${Math.trunc(normalizedActionPlanId)}`);

    const siteId = this.getCurrentSiteId();
    if (siteId) {
      params = params.set('siteId', siteId);
    }

    return this.#http
      .get<{ data?: Array<{ actionPlanUniqueId?: number; actionPlanId?: number }> }>(`${this.#apiActionPlan}/ActionPlan/search/list`, {
        params,
        headers: this.#getEnvironmentHeaders(),
      })
      .pipe(
        map((response) => response?.data ?? []),
        map((items) => {
          const matchedItem = items.find((item) => Number(item?.actionPlanId ?? 0) === Math.trunc(normalizedActionPlanId));
          const resolvedUniqueId = Number(matchedItem?.actionPlanUniqueId ?? 0);
          return Number.isFinite(resolvedUniqueId) && resolvedUniqueId > 0 ? resolvedUniqueId : null;
        }),
        catchError(() => of(null))
      );
  }

  public getActionPlanById(actionPlanUniqueId: number, includeDraft = false): Observable<SvcModalActionPlanByIdResponse> {
    // Drafts are not yet definitive plans; the endpoint only returns them when
    // `includeDraft=true` is sent, otherwise it answers 404.
    const params = includeDraft ? new HttpParams().set('includeDraft', true) : undefined;
    return this.#http.get<SvcModalActionPlanByIdResponse>(`${this.#apiActionPlan}/ActionPlan/${actionPlanUniqueId}`, {
      headers: this.#getEnvironmentHeaders(),
      params,
    });
  }

  public getSystemById(systemUniqueId: number): Observable<SvcModalActionPlanSystem> {
    return this.#http.get<SvcModalActionPlanSystem>(`${this.#apiActionPlan}/System/${systemUniqueId}`, {
      headers: this.#getEnvironmentHeaders(),
    });
  }

  public isCustomFieldTemplateActive(templateId: number | null | undefined, applicationId: string): Observable<boolean> {
    const normalizedTemplateId = Number(templateId ?? 0);
    if (!Number.isFinite(normalizedTemplateId) || normalizedTemplateId <= 0) return of(false);

    const normalizedApplicationId = String(applicationId ?? '').trim();
    if (!normalizedApplicationId) return of(false);

    return this.#getActiveTemplatesBySiteAndApplicationId(normalizedApplicationId).pipe(
      map((templates) =>
        (templates ?? []).some((template) => {
          const currentTemplateId = Number(template?.templateId ?? 0);
          if (!Number.isFinite(currentTemplateId) || currentTemplateId <= 0) return false;
          return currentTemplateId === normalizedTemplateId && this.#isTemplateActive(template);
        })
      ),
      catchError(() => of(false))
    );
  }

  public updateActionPlan(
    actionPlanUniqueId: number,
    currentActionPlan: SvcModalActionPlanByIdResponse,
    payload: SvcModalActionPlanCreateRequest,
    overrides?: Partial<Pick<SvcModalActionPlanUpdateApiRequest, 'actionPlanColorCode'>>,
    includeDraft = false
  ): Observable<unknown> {
    const params = includeDraft ? new HttpParams().set('includeDraft', true) : undefined;
    return this.#http.put<unknown>(
      `${this.#apiActionPlan}/ActionPlan/${actionPlanUniqueId}`,
      this.#mapUpdateRequestPayload(currentActionPlan, payload, overrides),
      {
        headers: this.#getEnvironmentHeaders(),
        params,
      }
    );
  }

  public deleteActionPlan(actionPlanUniqueId: number): Observable<boolean> {
    return this.#http.delete<boolean>(`${this.#apiActionPlan}/ActionPlan/${actionPlanUniqueId}`, {
      headers: this.#getEnvironmentHeaders(),
    });
  }

  public cancelActionPlan(actionPlanUniqueId: number): Observable<boolean> {
    return this.#http.put<boolean>(
      `${this.#apiActionPlan}/ActionPlan/cancel`,
      { actionPlanUniqueId: Number(actionPlanUniqueId ?? 0) },
      { headers: this.#getEnvironmentHeaders() }
    );
  }

  public rescheduleActionPlan(actionPlanUniqueId: number, payload: SvcModalActionPlanRescheduleRequest): Observable<boolean> {
    return this.#http.post<boolean>(`${this.#apiActionPlan}/ActionPlan/${actionPlanUniqueId}/reschedule`, this.#mapRescheduleRequestPayload(payload), {
      headers: this.#getEnvironmentHeaders(),
    });
  }

  public escalateActionPlan(actionPlanUniqueId: number, payload: SvcModalActionPlanEscalateRequest): Observable<boolean> {
    return this.#http.post<boolean>(`${this.#apiActionPlan}/ActionPlan/${actionPlanUniqueId}/escalate`, this.#mapEscalateRequestPayload(payload), {
      headers: this.#getEnvironmentHeaders(),
    });
  }

  #getSystems(): Observable<SvcModalActionPlanSystem[]> {
    if (this.#systemsCache$) return this.#systemsCache$;

    const params = new HttpParams()
      .set('pageIndex', 1)
      .set('pageSize', 500)
      .set('orderBy', 'SystemName')
      .set('orderDirection', 'Asc');

    this.#systemsCache$ = this.#http
      .get<SvcModalActionPlanSystemsResponse>(`${this.#apiActionPlan}/System`, {
        params,
        headers: this.#getEnvironmentHeaders(),
      })
      .pipe(
        map((response) => response?.data ?? []),
        shareReplay(1)
      );

    return this.#systemsCache$;
  }

  #getTypeOptions(): Observable<ISvcChipOption[]> {
    return this.#http
      .get<SvcModalActionPlanTypeOption[] | { data?: SvcModalActionPlanTypeOption[] }>(`${this.#apiActionPlan}/Type/List/Global`, {
        headers: this.#getEnvironmentHeaders(),
      })
      .pipe(
        map((response) => (Array.isArray(response) ? response : response?.data ?? [])),
        map((types) => this.#mapTypesToChipOptions(types)),
        catchError(() => of(this.#getTypeOptionsFallback()))
      );
  }

  #getTypeOptionsFallback(): ISvcChipOption[] {
    const options: SvcModalActionPlanTypeOption[] = [
      { typeId: 1, description: 'Corretivo', active: true },
      { typeId: 2, description: 'Preventivo', active: true },
      { typeId: 3, description: 'Outros', active: true },
    ];

    return this.#mapTypesToChipOptions(options);
  }

  #mapTypesToChipOptions(options: SvcModalActionPlanTypeOption[]): ISvcChipOption[] {
    return (options ?? []).flatMap((option) => {
      const normalizedTypeId = Number(option?.typeId ?? NaN);
      if (!Number.isFinite(normalizedTypeId) || normalizedTypeId <= 0) return [];

      return [
        {
          label: this.#translocoService.translate(option.description),
          value: normalizedTypeId,
        },
      ];
    });
  }

  #getPriorityOptions(): Observable<ISvcChipOption[]> {
    const params = new HttpParams()
      .set('pageIndex', 1)
      .set('pageSize', 500)
      .set('orderBy', 'PriorityDescription')
      .set('orderDirection', 'Asc');

    return this.#http
      .get<SvcModalActionPlanPrioritiesResponse>(`${this.#apiActionPlan}/Priority`, {
        params,
        headers: this.#getEnvironmentHeaders(),
      })
      .pipe(
        map((response) => response?.data ?? []),
        map((priorities) => priorities.filter((priority) => !!priority?.isActive)),
        map((priorities) =>
          priorities.flatMap((priority) => {
            const priorityId = Number(priority?.priorityUniqueId ?? Number.NaN);
            if (!Number.isFinite(priorityId) || priorityId <= 0) return [];

            return [
              {
                label: priority?.priorityDescription ?? '',
                value: priorityId,
              },
            ];
          })
        ),
        catchError(() => of([]))
      );
  }

  #getUsersOptions(): Observable<ISvcChipOption[]> {
    return this.#getUsersOptionsFromUserInfo().pipe(
      catchError(() => this.#getCurrentUserOptionFallback()),
      map((users) => this.#removeDuplicatedUsers(users))
    );
  }

  #getUsersOptionsFromUserInfo(): Observable<ISvcChipOption[]> {
    return this.#userService
      .getUsers(true)
      .pipe(
        map((users) =>
          (users ?? []).map((user) => ({
            label: user?.name,
            value: user?.userId,
          }))
        )
      );
  }

  /**
   * Fetches active teams to feed the "Atores" picker. Mirrors the
   * `paginated /Team` contract already used by `svc-modal-bos` /
   * `svc-modal-rca` combos — single page with a high `pageSize` so the
   * dropdown ships the full list. Falls back to an empty array on failure
   * so the modal still renders the users group.
   */
  #getTeamsOptions(): Observable<ISvcChipOption[]> {
    return this.#userService
      .getTeamsInfo({
        pageSize: 9999999,
        pageIndex: 1,
        active: true,
        name: '',
      })
      .pipe(
        map((response: { teams?: Array<{ teamId: number; name: string }> } | null | undefined) =>
          (response?.teams ?? []).map((team) => ({
            label: team?.name ?? '',
            value: team?.teamId,
          }))
        ),
        catchError(() => of([] as ISvcChipOption[]))
      );
  }

  #getActiveTemplatesBySiteAndApplicationId(applicationId: string): Observable<SvcModalActionPlanCustomFieldTemplate[]> {
    const siteId = Number(this.getCurrentSiteId() ?? 0);
    if (!Number.isFinite(siteId) || siteId <= 0 || !this.#apiCustomField) {
      return of([]);
    }

    const params = new HttpParams()
      .set('siteId', `${siteId}`)
      .set('applicationId', applicationId)
      .set('isActive', 'true');

    return this.#http
      .get<SvcModalActionPlanCustomFieldTemplate[] | { data?: SvcModalActionPlanCustomFieldTemplate[] }>(
        `${this.#apiCustomField}/Template/BySiteAndApplicationId`,
        { params }
      )
      .pipe(
        map((response) => (Array.isArray(response) ? response : response?.data ?? [])),
        catchError(() => of([]))
      );
  }

  #isTemplateActive(template: SvcModalActionPlanCustomFieldTemplate): boolean {
    if (typeof template?.isActive === 'boolean') return template.isActive;

    const normalizedStatus = String(template?.status ?? '').trim().toLowerCase();
    return normalizedStatus === 'ativo' || normalizedStatus === 'active';
  }

  #getCurrentUserOptionFallback(): Observable<ISvcChipOption[]> {
    return of([
      {
        label: this.#userService.user?.firstLastName,
        value: this.#userService.userId$,
      },
    ] as ISvcChipOption[]);
  }

  #removeDuplicatedUsers(users: ISvcChipOption[]): ISvcChipOption[] {
    const mapByValue = new Map<string, ISvcChipOption>();

    (users ?? []).forEach((user) => {
      const key = String(user?.value ?? '');
      if (!key || mapByValue.has(key)) return;
      mapByValue.set(key, user);
    });

    return Array.from(mapByValue.values());
  }

  #mapCreateRequestPayload(payload: SvcModalActionPlanCreateRequest): SvcModalActionPlanCreateApiRequest {
    const expectedDate = this.#toApiDate(payload?.expectedDate);
    const supplierId = this.#resolveSupplierId(payload?.siteId);
    const originatorUserId = payload?.originatorId ?? this.#userService.userId$ ?? null;
    const originatorAreaId = this.#resolveOriginatorAreaId(payload?.originatorAreaId);
    const creationDate = this.#toApiDate(payload?.creationDate) ?? this.#localTodayApiDate();

    return {
      actionPlanId: this.#resolveCreateActionPlanId(payload?.actionPlanId),
      actionPlanName: payload?.title?.trim?.() ?? '',
      typeId: payload?.typeId ?? null,
      statusId: SvcModalActionPlanStatusId.NoPrazo,
      priorityUniqueId: payload?.priorityId ?? null,
      systemUniqueId: payload?.systemId ?? null,
      supplierId: Number.isFinite(supplierId) ? supplierId : 0,
      originatorUserId,
      originatorAreaId,
      responsibleUserId: payload?.responsibleId ?? null,
      // Backend contract: `approverUsers` carries who CAN approve. The
      // singular `approverUserId` (= who actually approved) is set
      // server-side on the approve action and NOT written from create/update.
      approverUsers: this.#resolveApproverUsersForCreate(payload),
      approverUserId: null,
      creationDate,
      initialPrevisionDate: expectedDate,
      actualPrevisionDate: expectedDate,
      approvalDate: null,
      conclusionDate: null,
      isClassified: !!payload?.confidential,
      isUnderApproval: this.#resolveIsUnderApprovalForCreate(payload),
      isDraft: !!payload?.isDraft,
      quantityReprogramming: 0,
      phaseId: null,
      actionPlanColorCode: '',
      areaId: null,
      outlookEventId: '',
      outlookDelete: '',
      cancelationDate: null,
      userOwners: this.#normalizeUserIds(payload?.actorIds),
      areaOwners: [],
      teamOwners: this.#normalizeTeamIds(payload?.teamOwners),
      isObeya: !!payload?.isObeya,
      // Widget-creation provenance (US-59381). Mirrors the RCA contract:
      // `systemReferences` travels only on the create payload — never on
      // update — so the backend can attribute the plan to the originating
      // module/widget (Obeya, Defect Tag, Room Board, ...).
      ...this.#buildSystemReferencesPayload(payload),
    };
  }

  #buildSystemReferencesPayload(
    payload: SvcModalActionPlanCreateRequest,
  ): Pick<SvcModalActionPlanCreateApiRequest, 'systemReferences'> {
    // Backend contract: each reference carries only `referenceValue` /
    // `referenceTypeId`. The owning application is no longer sent per
    // reference (the system is identified by the top-level `systemUniqueId`).
    const systemReferences = this.#resolveSystemReferences(payload)
      .map((reference) => ({ referenceValue: reference.referenceValue, referenceTypeId: reference.referenceTypeId }))
      .filter((reference) => this.#isValidSystemReference(reference));
    return systemReferences.length ? { systemReferences } : {};
  }

  #resolveSystemReferences(payload: SvcModalActionPlanCreateRequest): SvcModalActionPlanSystemReference[] {
    const explicit = (payload?.systemReferences ?? []).filter((reference) => this.#isValidSystemReference(reference));
    if (explicit.length) return explicit;

    const single: SvcModalActionPlanSystemReference = {
      applicationId: payload?.applicationId,
      referenceValue: payload?.referenceValue ?? undefined,
      referenceTypeId: payload?.referenceTypeId ?? undefined,
    };
    return this.#isValidSystemReference(single) ? [single] : [];
  }

  #isValidSystemReference(reference: SvcModalActionPlanSystemReference | null | undefined): boolean {
    if (!reference) return false;
    return (
      !!reference.applicationId ||
      (reference.referenceValue !== undefined && reference.referenceValue !== null) ||
      (reference.referenceTypeId !== undefined && reference.referenceTypeId !== null)
    );
  }

  #mapUpdateRequestPayload(
    currentActionPlan: SvcModalActionPlanByIdResponse,
    payload: SvcModalActionPlanCreateRequest,
    overrides?: Partial<Pick<SvcModalActionPlanUpdateApiRequest, 'actionPlanColorCode'>>
  ): SvcModalActionPlanUpdateApiRequest {
    const expectedDate = this.#toApiDate(payload?.expectedDate);
    const supplierId = this.#resolveSupplierId(currentActionPlan?.supplierId ?? currentActionPlan?.siteId);
    const originatorUserId = payload?.originatorId ?? currentActionPlan?.originatorUserId ?? this.#userService.userId$ ?? null;
    const creationDate = this.#toApiDate(currentActionPlan?.creationDate) ?? this.#localTodayApiDate();
    const initialPrevisionDate =
      this.#toApiDate(currentActionPlan?.initialPrevisionDate) ??
      expectedDate ??
      creationDate;
    const actualPrevisionDate =
      expectedDate ??
      this.#toApiDate(currentActionPlan?.actualPrevisionDate) ??
      initialPrevisionDate;
    const resolvedTypeId = this.#resolveNumericField(payload?.typeId ?? currentActionPlan?.typeId, 0);
    const resolvedPriorityId = this.#resolveNumericField(payload?.priorityId ?? currentActionPlan?.priorityUniqueId, 0);
    const resolvedSystemUniqueId = this.#resolveNumericField(payload?.systemId ?? currentActionPlan?.systemUniqueId, 0);
    const resolvedAreaId = this.#resolveNullablePositiveNumericField(currentActionPlan?.areaId);
    const resolvedOriginatorAreaId = this.#resolveOriginatorAreaId(currentActionPlan?.originatorAreaId);
    const resolvedPhaseId = this.#resolveNullableNumericField(currentActionPlan?.phaseId);

    return {
      actionPlanId: Number(currentActionPlan?.actionPlanId ?? 0),
      actionPlanName: payload?.title?.trim?.() ?? currentActionPlan?.actionPlanName ?? '',
      typeId: resolvedTypeId,
      statusId: currentActionPlan?.statusId ?? 0,
      priorityUniqueId: resolvedPriorityId,
      systemUniqueId: resolvedSystemUniqueId,
      supplierId,
      originatorUserId,
      originatorAreaId: resolvedOriginatorAreaId,
      responsibleUserId: payload?.responsibleId ?? currentActionPlan?.responsibleUserId ?? originatorUserId,
      // Backend contract: `approverUsers` carries who CAN approve (mutated
      // here). `approverUserId` carries who actually approved — preserved
      // verbatim from the loaded action plan; never written by the frontend.
      approverUsers: this.#resolveApproverUsersForUpdate(currentActionPlan, payload),
      approverUserId: currentActionPlan?.approverUserId ?? null,
      creationDate,
      initialPrevisionDate,
      actualPrevisionDate,
      approvalDate: this.#toApiDate(currentActionPlan?.approvalDate),
      conclusionDate: this.#toApiDate(currentActionPlan?.conclusionDate),
      isClassified: !!payload?.confidential,
      isUnderApproval: this.#resolveIsUnderApprovalForUpdate(currentActionPlan, payload),
      quantityReprogramming: Number(currentActionPlan?.quantityReprogramming ?? 0),
      phaseId: resolvedPhaseId,
      actionPlanColorCode: overrides?.actionPlanColorCode ?? currentActionPlan?.actionPlanColorCode ?? null,
      areaId: resolvedAreaId,
      outlookEventId: currentActionPlan?.outlookEventId ?? null,
      outlookDelete: this.#resolveOutlookDelete(currentActionPlan?.outlookDelete),
      cancelationDate: this.#toApiDate(currentActionPlan?.cancelationDate),
      userOwners: this.#normalizeUserIds(payload?.actorIds),
      areaOwners: [],
      teamOwners: this.#normalizeTeamIds(payload?.teamOwners),
    };
  }

  #resolveSupplierId(siteId?: number | null): number {
    const supplierId = Number(siteId ?? this.getCurrentSiteId() ?? 0);
    return Number.isFinite(supplierId) ? supplierId : 0;
  }

  #resolveOriginatorAreaId(originatorAreaId?: number | null): number | null {
    const resolvedAreaId = Number(originatorAreaId ?? null);
    if (Number.isFinite(resolvedAreaId) && resolvedAreaId > 0) return resolvedAreaId;

    // Backend contract for Action Plan create currently expects a non-null area id (default 1).
    return 1;
  }

  #resolveCreateActionPlanId(actionPlanId?: number | null): number {
    const resolvedActionPlanId = Number(actionPlanId ?? null);
    if (Number.isFinite(resolvedActionPlanId) && resolvedActionPlanId > 0) {
      return Math.trunc(resolvedActionPlanId);
    }

    // API currently expects a valid identifier at creation time.
    // Uses a 6-digit random number (100000–999999) instead of a timestamp to keep IDs short.
    return Math.floor(Math.random() * 900_000) + 100_000;
  }

  #resolveNumericField(value: number | null | undefined, fallback: number): number {
    const parsedValue = Number(value ?? fallback);
    return Number.isFinite(parsedValue) ? parsedValue : fallback;
  }

  #resolveNullableNumericField(value: number | null | undefined): number | null {
    const parsedValue = Number(value ?? null);
    return Number.isFinite(parsedValue) ? parsedValue : null;
  }

  #resolveNullablePositiveNumericField(value: number | null | undefined): number | null {
    const parsedValue = Number(value ?? null);
    return Number.isFinite(parsedValue) && parsedValue > 0 ? parsedValue : null;
  }

  #resolveOutlookDelete(outlookDelete?: string | boolean | null): string | null {
    if (typeof outlookDelete === 'string') return outlookDelete;
    if (typeof outlookDelete === 'boolean') return String(outlookDelete);
    return null;
  }

  #normalizeUserIds(userIds?: Array<string | null | undefined>): string[] {
    const normalizedUserIds = (Array.isArray(userIds) ? userIds : [])
      .map((userId) => String(userId ?? '').trim())
      .filter((userId) => !!userId);

    return Array.from(new Set(normalizedUserIds));
  }

  /**
   * Dedups and coerces the team-owner ids that flow from the chip-grouped
   * value (already decoded by the form via `decodeOwnerSelection`) into the
   * numeric array the API expects. Mirrors `#normalizeUserIds` semantics.
   */
  #normalizeTeamIds(teamIds?: Array<number | string | null | undefined> | null): number[] {
    const normalized = (Array.isArray(teamIds) ? teamIds : [])
      .map((teamId) => Number(teamId))
      .filter((teamId) => Number.isFinite(teamId) && teamId !== 0);

    return Array.from(new Set(normalized));
  }

  /**
   * Resolves the multi-approver list for create payloads — reads the
   * `approverUsers` array from the form payload (= who can approve).
   */
  #resolveApproverUsersForCreate(payload: SvcModalActionPlanCreateRequest | null | undefined): string[] {
    return Array.isArray(payload?.approverUsers) ? this.#normalizeUserIds(payload?.approverUsers) : [];
  }

  #resolveIsUnderApprovalForCreate(payload: SvcModalActionPlanCreateRequest | null | undefined): boolean {
    return this.#resolveApproverUsersForCreate(payload).length > 0;
  }

  /**
   * Resolves the multi-approver list for update payloads. Mirrors the
   * "explicit-undefined ⇒ keep current" behaviour: if the caller did not
   * include `approverUsers` on the payload, we keep whatever the loaded
   * action plan already had.
   */
  #resolveApproverUsersForUpdate(
    currentActionPlan: SvcModalActionPlanByIdResponse | null | undefined,
    payload: SvcModalActionPlanCreateRequest | null | undefined,
  ): string[] {
    if (Array.isArray(payload?.approverUsers)) {
      return this.#normalizeUserIds(payload?.approverUsers);
    }

    return Array.isArray(currentActionPlan?.approverUsers)
      ? this.#normalizeUserIds(currentActionPlan?.approverUsers)
      : [];
  }

  #resolveIsUnderApprovalForUpdate(
    currentActionPlan: SvcModalActionPlanByIdResponse | null | undefined,
    payload: SvcModalActionPlanCreateRequest | null | undefined,
  ): boolean {
    if (Array.isArray(payload?.approverUsers)) {
      return this.#resolveApproverUsersForUpdate(currentActionPlan, payload).length > 0;
    }

    return !!currentActionPlan?.isUnderApproval;
  }

  #mapRescheduleRequestPayload(payload: SvcModalActionPlanRescheduleRequest): { previsionDate: string | null; comments: string | null } {
    return {
      previsionDate: this.#toApiDate(payload?.previsionDate),
      comments: payload?.comments?.trim() || null,
    };
  }

  #mapEscalateRequestPayload(payload: SvcModalActionPlanEscalateRequest): SvcModalActionPlanEscalateRequest {
    const mapped: SvcModalActionPlanEscalateRequest = {
      newResponsibleUserId: payload?.newResponsibleUserId,
      newSystemUniqueId: Number(payload?.newSystemUniqueId ?? 0),
      comments: payload?.comments ?? null,
      isObeya: !!payload?.isObeya,
      siteId: Number(payload?.siteId ?? this.getCurrentSiteId() ?? 0),
    };

    if (payload?.systemReferences?.length) mapped.systemReferences = payload.systemReferences;

    return mapped;
  }

  #localTodayApiDate(): string {
    return `${formatDate(new Date(), 'yyyy-MM-dd', 'en-US')}T00:00:00Z`;
  }

  #toApiDate(value?: string | null): string | null {
    if (!value) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return `${value}T00:00:00Z`;
    }

    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString();
  }

  #getEnvironmentHeaders(): { EnvironmentUrl: string } {
    return {
      EnvironmentUrl: this.#resolveEnvironmentUrl(),
    };
  }

  #resolveEnvironmentUrl(): string {
    const fromStorage = typeof localStorage !== 'undefined' ? localStorage.getItem('environmentUrl') : null;
    const fromWindow = typeof window !== 'undefined' ? window.location.origin : '';
    return fromStorage || fromWindow;
  }
}
